import React from 'react';

import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListAllowLists } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowLists';
import { CURRENCIES } from 'src/features/payStations/types';
import ReservationNameTypeDate from 'src/features/reservations/components/forms/ReservationNameTypeDate';
import ReservationPricing from 'src/features/reservations/components/forms/ReservationPricing';
import ReservationStayDetails from 'src/features/reservations/components/forms/ReservationStayDetails';
import { type CreateReservationTypeFormSchemaValues } from 'src/features/reservations/hooks/useCreateReservationType';
import { useDisplayedFormValues } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { formatPrice } from 'src/features/reservations/utils/utils';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import InputFormField from 'src/registry/components/forms/InputFormField';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

interface VersionFormProps {
    form: UseFormReturn<CreateReservationTypeFormSchemaValues>;
    onSubmit: () => void;
}

function VersionForm({ form, onSubmit }: VersionFormProps) {
    const { setDisplayedFormValues } = useDisplayedFormValues();
    const { price, tax_amount, price_with_tax, tax_percent, currency, valid_from, paid } = form.getValues();
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const [selectedFromDate, setSelectedFromDate] = React.useState<Date | undefined>(valid_from);

    const { data: listAllowLists } = useListAllowLists({ parkingId });
    const { data: listSubParkings } = useListSubParkings({ parkingId });

    React.useEffect(() => {
        setDisplayedFormValues({
            price: price ? formatPrice(price) : '',
            tax_percent: tax_percent ? (parseFloat(tax_percent) * 100).toFixed(2) : '',
            tax_amount: tax_amount ? formatPrice(tax_amount) : '',
            price_with_tax: price_with_tax ? formatPrice(price_with_tax) : '',
            currency: currency ?? CURRENCIES.EUR,
        });
    }, [form]);

    const BooleanOptions = {
        false: formatMessage(generalMessages.no),
        true: formatMessage(generalMessages.yes),
    };

    return (
        <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
            <ReservationNameTypeDate selectedFromDate={selectedFromDate} setSelectedFromDate={setSelectedFromDate} />
            <FormField
                control={form.control}
                name="paid"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel required variant="dialog">
                                {formatMessage(reservationMessages.paid)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) => form.setValue('paid', val === 'true', { shouldDirty: true })}
                                        value={(paid ?? true).toString()}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={formatMessage(reservationMessages.paid)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(BooleanOptions).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        {value}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <MultiSelectFormField<CreateReservationTypeFormSchemaValues>
                label={formatMessage(reservationMessages.zones)}
                form={form}
                options={listSubParkings?.results}
                name="zones"
                required={false}
            />
            <MultiSelectFormField<CreateReservationTypeFormSchemaValues>
                label={formatMessage(reservationMessages.allowLists)}
                form={form}
                options={listAllowLists?.results}
                name="allow_lists"
                required={false}
            />
            <InputFormField<CreateReservationTypeFormSchemaValues>
                label={formatMessage(reservationMessages.maxDuration)}
                form={form}
                type="number"
                name="max_duration"
                required={false}
            />
            <InputFormField<CreateReservationTypeFormSchemaValues>
                label={formatMessage(reservationMessages.maxStartDateDays)}
                form={form}
                type="number"
                name="max_start_date_days"
                required={false}
            />
            {paid && <ReservationPricing />}
            <ReservationStayDetails />
        </Form>
    );
}

export { VersionForm };
